import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import type {
  ActionComment,
  ActionDetail,
  ActionListItem,
  ActionStatus,
  CreateActionPayload,
  PaginatedResponse,
  UpdateActionPayload,
} from "../types";

const ACTIONS_KEY = ["actions"] as const;

export type ActionListParams = {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  priority?: string;
  source?: string;
  companyId?: string;
  productId?: string;
  projectId?: string;
  phaseId?: string;
  stageId?: string;
  meetingId?: string;
  taskId?: string;
  decisionId?: string;
  milestoneId?: string;
  assignedToId?: string;
  overdue?: boolean;
};

export function useActionList(
  params: ActionListParams = {},
): UseQueryResult<PaginatedResponse<ActionListItem>> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...ACTIONS_KEY, "list", params, accessToken ? "auth" : "anon"],
    enabled: !!accessToken,
    queryFn: () =>
      apiFetch<PaginatedResponse<ActionListItem>>("/actions", {
        token: accessToken,
        query: {
          page: params.page,
          limit: params.limit,
          search: params.search,
          status: params.status,
          priority: params.priority,
          source: params.source,
          companyId: params.companyId,
          productId: params.productId,
          projectId: params.projectId,
          phaseId: params.phaseId,
          stageId: params.stageId,
          meetingId: params.meetingId,
          taskId: params.taskId,
          decisionId: params.decisionId,
          milestoneId: params.milestoneId,
          assignedToId: params.assignedToId,
          overdue: params.overdue ? "true" : undefined,
        },
      }),
  });
}

export function useAction(id: string): UseQueryResult<ActionDetail> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...ACTIONS_KEY, "detail", id, accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!id,
    queryFn: async () => {
      const res = await apiFetch<{ action: ActionDetail }>(`/actions/${id}`, {
        token: accessToken,
      });
      return res.action;
    },
  });
}

export function useCreateAction() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateActionPayload) =>
      apiFetch<{ action: ActionDetail }>("/actions", {
        method: "POST",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...ACTIONS_KEY] });
      qc.invalidateQueries({ queryKey: ["tasks"] });
      qc.invalidateQueries({ queryKey: ["meetings"] });
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["companies"] });
      qc.invalidateQueries({ queryKey: ["products"] });
    },
  });
}

export function useUpdateAction(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateActionPayload) =>
      apiFetch<{ action: ActionDetail }>(`/actions/${id}`, {
        method: "PUT",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...ACTIONS_KEY] });
    },
  });
}

export type UpdateActionStatusResult = {
  action: ActionDetail;
  allTaskActionsDone?: boolean;
  taskId?: string;
  taskCode?: string;
};

export function useUpdateActionStatus() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation<
    UpdateActionStatusResult,
    Error,
    { id: string; status: ActionStatus }
  >({
    mutationFn: async ({ id, status }) =>
      apiFetch<UpdateActionStatusResult>(`/actions/${id}/status`, {
        method: "PUT",
        body: { status },
        token: accessToken,
      }),
    onSuccess: (res, vars) => {
      qc.invalidateQueries({ queryKey: [...ACTIONS_KEY] });
      qc.invalidateQueries({ queryKey: [...ACTIONS_KEY, "detail", vars.id] });
      if (res.allTaskActionsDone && res.taskId) {
        qc.invalidateQueries({ queryKey: ["tasks"] });
        qc.invalidateQueries({ queryKey: ["tasks", "detail", res.taskId] });
      }
    },
  });
}

export function useDeleteAction() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/actions/${id}`, { method: "DELETE", token: accessToken }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...ACTIONS_KEY] });
      qc.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
}

export function useActionComments(actionId: string) {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...ACTIONS_KEY, actionId, "comments", accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!actionId,
    queryFn: async () => {
      const res = await apiFetch<{ data: ActionComment[] }>(
        `/actions/${actionId}/comments`,
        { token: accessToken },
      );
      return res.data;
    },
  });
}

export function useAddActionComment(actionId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (content: string) =>
      apiFetch<{ comment: ActionComment }>(`/actions/${actionId}/comments`, {
        method: "POST",
        body: { content },
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [...ACTIONS_KEY, actionId, "comments"],
      });
    },
  });
}

export function useDeleteActionComment(actionId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) =>
      apiFetch(`/actions/${actionId}/comments/${commentId}`, {
        method: "DELETE",
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [...ACTIONS_KEY, actionId, "comments"],
      });
    },
  });
}
